import { format, formatDistanceToNow } from "date-fns";
import { es as esLocale, enUS as enLocale } from "date-fns/locale";
import { VIDEO_FORMATS } from "../../utils/constants";
import { isCarouselItem } from "../../utils/carouselUtils";
import { getExtensionFile } from "../../utils/string";
import {
  hasMultipleApprovalReviewers,
  getApprovalReviewersList,
  mergeReviewersWithApprovals,
  mergeReviewerApprovalsIntoContentMap,
} from "./approvalReviewersUtils";

const APPROVED_CONTENT_APPROVAL_STATUS = "approved";
const REVIEWABLE_CONTENT_APPROVAL_STATUSES = new Set(["pending", "rejected"]);
const normalizeContentApprovalContentID = (contentID) =>
  String(contentID || "");
const getContentApprovalContentID = (item = {}) =>
  item?.contentID || item?.id || "";

const getDateFnsLocale = (language) => {
  if (language?.startsWith("es")) {
    return esLocale;
  }
  return enLocale;
};

const toDate = (timestamp) => {
  if (!timestamp) {
    return null;
  }

  try {
    return new Date(
      typeof timestamp === "number" && timestamp < 1e12
        ? timestamp * 1000
        : timestamp
    );
  } catch {
    return null;
  }
};

export const resolveContentApprovalMediaType = (item = {}) => {
  if (isCarouselItem(item) || item?.type === "carousel") {
    return "carousel";
  }

  if (item?.type === "video") {
    return "video";
  }

  const extension = getExtensionFile(item?.url || "");
  if (VIDEO_FORMATS.includes(extension)) {
    return "video";
  }

  return "image";
};

export const getContentApprovalStateForContent = ({
  approvalData = null,
  contentID = "",
  fallbackStatus = null,
  fallbackCurrentDraftID = null,
  fallbackDraftRevision = 0,
} = {}) => {
  const normalizedContentID = normalizeContentApprovalContentID(contentID);
  const nestedState = normalizedContentID
    ? approvalData?.contents?.[normalizedContentID]?.states ||
      approvalData?.contents?.[contentID]?.states ||
      null
    : null;
  const contentState = normalizedContentID
    ? approvalData?.contentStates?.[normalizedContentID] ||
      approvalData?.contentStates?.[contentID] ||
      nestedState ||
      {}
    : {};

  return {
    status:
      contentState?.status ?? fallbackStatus ?? approvalData?.status ?? null,
    currentDraftID:
      contentState?.currentDraftID ??
      fallbackCurrentDraftID ??
      approvalData?.currentDraftID ??
      "",
    draftRevision:
      Number(
        contentState?.draftRevision ??
          fallbackDraftRevision ??
          approvalData?.draftRevision
      ) || 0,
  };
};

export const getReviewerApprovalsForContent = ({
  approvalData = null,
  contentID = "",
} = {}) => {
  const normalizedContentID = normalizeContentApprovalContentID(contentID);
  if (!normalizedContentID || !approvalData) {
    return {};
  }

  const fromNormalizedMap =
    approvalData?.reviewerApprovalsByContent?.[normalizedContentID];
  if (fromNormalizedMap) {
    return fromNormalizedMap;
  }

  const nestedApprovals =
    approvalData?.contents?.[normalizedContentID]?.reviewerApprovals ||
    approvalData?.contents?.[contentID]?.reviewerApprovals;

  if (!nestedApprovals) {
    return {};
  }

  return mergeReviewerApprovalsIntoContentMap({
    currentApprovals: {},
    reviewerApprovals: nestedApprovals,
  });
};

export const getReviewersWithStatusForContent = ({
  approvalData = null,
  contentID = "",
} = {}) => {
  if (!approvalData) {
    return [];
  }

  const reviewers = getApprovalReviewersList(approvalData);

  return mergeReviewersWithApprovals({
    reviewers,
    reviewerApprovals: getReviewerApprovalsForContent({
      approvalData,
      contentID,
    }),
  });
};

export const getReviewerOwnStatusForContent = ({
  approvalData = null,
  contentID = "",
  reviewerID = "",
  fallbackStatus = null,
  fallbackCurrentDraftID = null,
  fallbackDraftRevision = 0,
} = {}) => {
  const normalizedContentID = normalizeContentApprovalContentID(contentID);
  const normalizedReviewerID = String(reviewerID || "").trim();

  const globalStatus = getContentApprovalStateForContent({
    approvalData,
    contentID: normalizedContentID,
    fallbackStatus,
    fallbackCurrentDraftID,
    fallbackDraftRevision,
  }).status;

  if (!normalizedReviewerID || !approvalData) {
    return globalStatus;
  }

  const reviewers = getApprovalReviewersList(approvalData);

  if (!hasMultipleApprovalReviewers(reviewers)) {
    return globalStatus;
  }

  const reviewerApprovals = getReviewerApprovalsForContent({
    approvalData,
    contentID: normalizedContentID,
  });
  const ownApprovalStatus = reviewerApprovals[normalizedReviewerID]?.status;

  if (ownApprovalStatus) {
    return ownApprovalStatus;
  }

  return "pending";
};

export const mergeGalleryItemWithApprovalData = (item, approvalData) => {
  if (!item?.id || !approvalData) {
    return item;
  }

  const contentState = getContentApprovalStateForContent({
    approvalData,
    contentID: item.id,
    fallbackStatus: null,
    fallbackCurrentDraftID: null,
    fallbackDraftRevision: 0,
  });

  if (!contentState.status) {
    return item;
  }

  return {
    ...item,
    approvalStatus: contentState.status,
    currentDraftID: contentState.currentDraftID || null,
    draftRevision: contentState.draftRevision,
  };
};

export const buildContentApprovalPayloadFromGalleryItem = (
  item = {},
  approvalData = null
) => {
  const contentID = item?.id || null;
  const contentState = getContentApprovalStateForContent({
    approvalData,
    contentID,
    fallbackStatus: item?.approvalStatus || item?.status || null,
    fallbackCurrentDraftID: item?.currentDraftID || null,
    fallbackDraftRevision: item?.draftRevision || 0,
  });

  return {
    contentID,
    type: resolveContentApprovalMediaType(item),
    status: contentState.status,
    currentDraftID: contentState.currentDraftID,
    draftRevision: contentState.draftRevision,
  };
};

export const formatContentApprovalRelativeTime = (timestamp, language) => {
  const date = toDate(timestamp);
  if (!date) {
    return "";
  }

  try {
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: getDateFnsLocale(language),
    });
  } catch {
    return "";
  }
};

export const formatContentApprovalSentDate = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) {
    return "";
  }

  try {
    return format(date, "d MMM, yyyy");
  } catch {
    return "";
  }
};

export const hasContentApprovalCommentBody = (comment) =>
  Boolean(String(comment?.body ?? "").trim());

export const isReviewableContentApprovalStatus = (status) =>
  REVIEWABLE_CONTENT_APPROVAL_STATUSES.has(status);

export const getInitialVisibleContentApprovalContentIDs = (payloads = []) => {
  const allContentIDs = payloads
    .map((payload) => getContentApprovalContentID(payload))
    .filter(Boolean);
  const visibleContentIDs = payloads
    .filter((payload) => {
      const contentState = getContentApprovalStateForContent({
        contentID: getContentApprovalContentID(payload),
        fallbackStatus: payload?.status || payload?.approvalStatus || null,
      });

      return contentState.status !== APPROVED_CONTENT_APPROVAL_STATUS;
    })
    .map((payload) => getContentApprovalContentID(payload))
    .filter(Boolean);

  return visibleContentIDs.length > 0 ? visibleContentIDs : allContentIDs;
};

export const filterVisibleContentApprovalPayloads = (
  payloads = [],
  visibleContentIDs = []
) => {
  const visibleContentIDSet = new Set(
    (visibleContentIDs || [])
      .map((contentID) => normalizeContentApprovalContentID(contentID))
      .filter(Boolean)
  );

  if (visibleContentIDSet.size === 0) {
    return payloads;
  }

  return payloads.filter((payload) =>
    visibleContentIDSet.has(
      normalizeContentApprovalContentID(getContentApprovalContentID(payload))
    )
  );
};

export const getContentApprovalCurrentDraftIDsByContentID = (
  items = [],
  approvalData = null
) =>
  items.reduce((currentDraftIDsByContentID, item) => {
    const contentID = getContentApprovalContentID(item);
    if (!contentID) {
      return currentDraftIDsByContentID;
    }

    const contentState = getContentApprovalStateForContent({
      approvalData,
      contentID,
      fallbackStatus: item?.approvalStatus || item?.status || null,
      fallbackCurrentDraftID: item?.currentDraftID || null,
      fallbackDraftRevision: item?.draftRevision || 0,
    });

    if (!contentState.currentDraftID) {
      return currentDraftIDsByContentID;
    }

    return {
      ...currentDraftIDsByContentID,
      [normalizeContentApprovalContentID(contentID)]:
        contentState.currentDraftID,
    };
  }, {});

export const filterActiveContentApprovalComments = (
  comments = [],
  {
    contentID = "",
    contentIDs = [],
    currentDraftID = "",
    currentDraftIDsByContentID = {},
    types = ["general"],
    requireBody = true,
  } = {}
) => {
  const normalizedSelectedContentID =
    normalizeContentApprovalContentID(contentID);
  const contentIDSet = normalizedSelectedContentID
    ? new Set([normalizedSelectedContentID])
    : new Set(
        (contentIDs || [])
          .map((entry) => normalizeContentApprovalContentID(entry))
          .filter(Boolean)
      );

  return comments.filter((comment) => {
    if (requireBody && !hasContentApprovalCommentBody(comment)) {
      return false;
    }

    if (comment?.isStale) {
      return false;
    }

    if (
      Array.isArray(types) &&
      types.length > 0 &&
      !types.includes(comment?.type)
    ) {
      return false;
    }

    const normalizedCommentContentID = normalizeContentApprovalContentID(
      comment?.contentID
    );

    if (
      contentIDSet.size > 0 &&
      normalizedCommentContentID &&
      !contentIDSet.has(normalizedCommentContentID)
    ) {
      return false;
    }

    const matchingDraftID = normalizedCommentContentID
      ? currentDraftIDsByContentID?.[normalizedCommentContentID] ||
        currentDraftID
      : currentDraftID;

    return (
      !matchingDraftID ||
      !comment?.draftID ||
      comment?.draftID === matchingDraftID
    );
  });
};

export const getContentApprovalContentIDs = (approvalData = null) => {
  const fromStates = Object.keys(approvalData?.contentStates || {})
    .map((contentID) => normalizeContentApprovalContentID(contentID))
    .filter(Boolean);

  if (fromStates.length > 0) {
    return fromStates;
  }

  return Object.keys(approvalData?.contents || {})
    .map((contentID) => normalizeContentApprovalContentID(contentID))
    .filter(Boolean);
};

export const getContentApprovalCreativeNumber = (
  contentIDs = [],
  contentID = ""
) => {
  if (!contentID || !Array.isArray(contentIDs)) {
    return null;
  }

  const index = contentIDs.indexOf(contentID);
  return index >= 0 ? index + 1 : null;
};

const PENDING_CONTENT_APPROVAL_STATUS = "pending";

const getPayloadApprovalStatus = ({
  payload = {},
  approvalData = null,
  statusOverrides = {},
  reviewerID = "",
}) => {
  const contentID = getContentApprovalContentID(payload);
  const normalizedContentID = normalizeContentApprovalContentID(contentID);
  const overrideStatus = statusOverrides?.[normalizedContentID]?.status;

  if (overrideStatus !== undefined) {
    return overrideStatus;
  }

  if (reviewerID) {
    return getReviewerOwnStatusForContent({
      approvalData,
      contentID,
      reviewerID,
      fallbackStatus: payload?.status ?? null,
      fallbackCurrentDraftID: payload?.currentDraftID || null,
      fallbackDraftRevision: payload?.draftRevision || 0,
    });
  }

  const contentState = getContentApprovalStateForContent({
    approvalData,
    contentID,
    fallbackStatus: payload?.status ?? null,
    fallbackCurrentDraftID: payload?.currentDraftID || null,
    fallbackDraftRevision: payload?.draftRevision || 0,
  });

  return contentState.status;
};

export const countPendingContentApprovals = ({
  payloads = [],
  approvalData = null,
  statusOverrides = {},
  reviewerID = "",
}) =>
  payloads.filter(
    (payload) =>
      getPayloadApprovalStatus({
        payload,
        approvalData,
        statusOverrides,
        reviewerID,
      }) === PENDING_CONTENT_APPROVAL_STATUS
  ).length;

export const findContentApprovalPayloadIndex = (
  payloads = [],
  contentID = ""
) => {
  const normalizedContentID = normalizeContentApprovalContentID(contentID);
  if (!normalizedContentID || !payloads.length) {
    return -1;
  }

  return payloads.findIndex(
    (payload) =>
      normalizeContentApprovalContentID(
        getContentApprovalContentID(payload)
      ) === normalizedContentID
  );
};

export const findNextPendingContentApprovalIndex = ({
  payloads = [],
  approvalData = null,
  currentContentID = "",
  statusOverrides = {},
  reviewerID = "",
}) => {
  if (!payloads.length || !currentContentID) {
    return null;
  }

  const currentIndex = payloads.findIndex(
    (payload) => getContentApprovalContentID(payload) === currentContentID
  );

  if (currentIndex < 0) {
    return null;
  }

  for (let step = 1; step < payloads.length; step += 1) {
    const index = (currentIndex + step) % payloads.length;
    const status = getPayloadApprovalStatus({
      payload: payloads[index],
      approvalData,
      statusOverrides,
      reviewerID,
    });

    if (status === PENDING_CONTENT_APPROVAL_STATUS) {
      return index;
    }
  }

  return null;
};

export const PENDING_CONTENT_APPROVAL_REVIEW_STORAGE_KEY =
  "buzzly:pendingContentApprovalReview";
